import { Button } from "components/atoms";
import { FormInput } from "components/molecules";
import DeliveryCountryCard from "components/molecules/card/delivery-country-card/DeliveryCountryCard";
import Popup from "components/molecules/popup";
import DigitalDeliveryCountryPopup from "components/molecules/popup/digital-delivery-country/DigitalDeliveryCountryPopup";
import WarningPopup from "components/molecules/popup/warning-popup/WarningPopup";
import { useFormikContext } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PiPaperPlaneTilt,
  PiPlus,
  PiWarning,
  PiX,
  PiXCircle,
} from "react-icons/pi";
import { PopupActions } from "reactjs-popup/dist/types";
import {
  AsapType,
  IDigitalDeliveryBase,
  IDigitalDeliveryForm,
} from "services/digital-delivery/types";
import { DELIVERY_STATUS } from "services/music-product-digital/types/enums";
import { BUTTON_VARIANT } from "types/components/button/button.interface";
import { useEnumToSelectOptions } from "utils/components/inputs/select";
import { useIsUserBanned } from "hooks/useIsUserBanned";

interface IDigitalDeliveryFormProps {
  onSubmitDelivery?: (actions: PopupActions) => void;
  onCancelDelivery?: (actions: PopupActions) => void;
  initialCountry: IDigitalDeliveryBase;
  values?: IDigitalDeliveryForm;
  initialValues?: IDigitalDeliveryForm;
  isReleaseDateValid?: boolean;
  minPublishDate?: string;
  formikPrefix?: string;
}

const DigitalDeliveryForm = ({
  onSubmitDelivery,
  onCancelDelivery,
  initialCountry,
  initialValues,
  values,
  isReleaseDateValid,
  minPublishDate,
  formikPrefix = "delivery.",
}: IDigitalDeliveryFormProps) => {
  const { t } = useTranslation();

  const { getSelectOptions } = useEnumToSelectOptions();
  const { setFieldValue } = useFormikContext();
  const { isBanned } = useIsUserBanned();

  const isInitialDeliveryEverywhere = useMemo(
    () => !!initialValues?.included.find((d) => d.countryCode === "ALL"),
    [values?.included],
  );
  const isDeliveryEverywhere = useMemo(
    () => !!values?.included.find((d) => d.countryCode === "ALL"),
    [values?.included],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col w-full gap-2 items-center">
        <p className="font-semibold opacity-65">
          {t("music-product-digital.delivery.where-to-send")}
        </p>
        <div className="flex gap-4 w-full">
          <Button
            fullWidth
            variant={BUTTON_VARIANT.white}
            isActive={isDeliveryEverywhere === true}
            onClick={() => {
              setFieldValue(
                `${formikPrefix}included`,
                isInitialDeliveryEverywhere
                  ? initialValues?.included
                  : [{ ...initialCountry, countryCode: "ALL" }],
              );
              setFieldValue(
                `${formikPrefix}excluded`,
                isInitialDeliveryEverywhere ? initialValues?.excluded : [],
              );
            }}
          >
            <Button.Text text={t("button.everywhere")} />
          </Button>
          <Button
            fullWidth
            variant={BUTTON_VARIANT.white}
            isActive={isDeliveryEverywhere === false}
            onClick={() => {
              setFieldValue(
                `${formikPrefix}included`,
                isInitialDeliveryEverywhere ? [] : initialValues?.included,
              );
              setFieldValue(`${formikPrefix}excluded`, []);
            }}
          >
            <Button.Text text={t("button.only-selected")} />
          </Button>
        </div>
      </div>
      {values && isDeliveryEverywhere === true && (
        <>
          <div className="flex flex-col w-full gap-2 items-center">
            <p className="font-semibold opacity-65">
              {t("music-product-digital.delivery.when-to-send")}
            </p>
            <div className="flex w-full gap-2">
              <FormInput.Select
                name={`${formikPrefix}included[0].asap`}
                options={getSelectOptions(AsapType, "enum.AsapType")}
                isClearable={false}
                displayMode={false}
                borderless
                fullWidth
              />
              {!isReleaseDateValid && (
                <WarningPopup
                  title={t("popup.header")}
                  message={t("music-product-digital.delivery.invalid-date", {
                    when: minPublishDate,
                  })}
                  trigger={
                    <Button onlyIcon variant={BUTTON_VARIANT.white}>
                      <Button.Icon icon={<PiWarning />} />
                    </Button>
                  }
                />
              )}
            </div>

            {values.included[0].asap === AsapType.None && (
              <div className="flex w-full gap-2">
                <FormInput.Text
                  label={t("input.label.date")}
                  name={`${formikPrefix}included[0].publishDate`}
                  type="date"
                  displayMode={false}
                  borderless
                  fullWidth
                />
                <div className="w-[250px]">
                  <FormInput.Text
                    label={t("input.label.time")}
                    name={`${formikPrefix}included[0].publishTime`}
                    type="time"
                    displayMode={false}
                    borderless
                    fullWidth
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col w-full gap-2 items-center">
            <p className="font-semibold opacity-65">
              {t("music-product-digital.delivery.need-exclude")}
            </p>
            <DigitalDeliveryCountryPopup
              type="excluded"
              initialCountry={initialCountry}
              disabledCountries={values?.excluded}
              onSave={(country, actions) => {
                setFieldValue(`${formikPrefix}excluded`, [
                  country,
                  ...(values?.excluded ?? []),
                ]);
                actions?.close();
              }}
              trigger={
                <Button fullWidth variant={BUTTON_VARIANT.white}>
                  <Button.Text text={t("button.exclude")} />
                  <Button.Icon icon={<PiX />} />
                </Button>
              }
            />
            {values?.excluded.map((e, key) => (
              <DeliveryCountryCard
                key={key}
                type="excluded"
                onSave={(country, actions) => {
                  setFieldValue(`${formikPrefix}excluded[${key}]`, country);
                  actions?.close();
                }}
                country={e}
                onRemove={() =>
                  setFieldValue(
                    `${formikPrefix}excluded`,
                    values?.excluded.filter(
                      (_, filterKey) => filterKey !== key,
                    ),
                  )
                }
              />
            ))}
          </div>
        </>
      )}
      {values && isDeliveryEverywhere === false && (
        <div className="flex flex-col w-full gap-2">
          <DigitalDeliveryCountryPopup
            type="included"
            initialCountry={initialCountry}
            disabledCountries={values.included}
            onSave={(country, actions) => {
              setFieldValue(`${formikPrefix}included`, [
                country,
                ...(values?.included ?? []),
              ]);
              actions?.close();
            }}
            trigger={
              <Button fullWidth variant={BUTTON_VARIANT.white}>
                <Button.Text text={t("button.select")} />
                <Button.Icon icon={<PiPlus />} />
              </Button>
            }
          />
          {values?.included.map((e, key) => (
            <DeliveryCountryCard
              key={key}
              type="included"
              onSave={(country, actions) => {
                setFieldValue(`${formikPrefix}included[${key}]`, country);
                actions?.close();
              }}
              country={e}
              onRemove={() =>
                setFieldValue(
                  `${formikPrefix}included`,
                  values?.included.filter((_, filterKey) => filterKey !== key),
                )
              }
            />
          ))}
        </div>
      )}{" "}
      <div />
      <div
        className="flex sticky gap-2 bottom-8 w-full max-w-[500px] p-8 rounded"
        style={{
          background: "var(--background)",
          filter: "drop-shadow(0 0 10px var(--app-background))",
        }}
      >
        {(values?.status === DELIVERY_STATUS.waitingForAccept ||
          values?.status === DELIVERY_STATUS.waiting) && (
          <Popup.CancelWarning
            name={
              values
                ? `${values.salesChannelName} - ${values.providerName}`
                : "-"
            }
            transI18nKey={"music-product-digital.delivery.cancel-warning"}
            onCancel={onCancelDelivery}
            trigger={
              <Button onlyIcon isClear>
                <Button.Icon icon={<PiXCircle />} />
                <Button.Description description={t("button.cancel")} />
              </Button>
            }
          />
        )}

        {!isBanned && (
          <Popup.WarningPopup
            confirmButtonVariant={BUTTON_VARIANT.submit}
            title=""
            message=""
            onConfirm={onSubmitDelivery}
            trigger={
              <Button
                fullWidth
                variant={BUTTON_VARIANT.submit}
                disabled={!values?.included.length}
              >
                <Button.Text
                  text={
                    values?.status === DELIVERY_STATUS.notSend
                      ? t("button.send-delivery")
                      : t("button.update-delivery")
                  }
                />
                <Button.Icon icon={<PiPaperPlaneTilt />} />
              </Button>
            }
          />
        )}
      </div>{" "}
    </div>
  );
};

export default DigitalDeliveryForm;
